import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Share2, Copy, Mail, MessageCircle, Linkedin, Facebook, Twitter, Send } from 'lucide-react';

/**
 * ShareButton — share a listing to WhatsApp / LinkedIn / Facebook / X / Telegram / Email / Copy.
 * Uses native navigator.share() on supported devices, otherwise opens platform sharer URL.
 * Fires POST /api/share/listing/:id?network=... in the background (non-blocking).
 *
 * Props:
 *  - listingId (string) — listing UUID
 *  - title (string) — listing title (used in share body)
 *  - url (string, optional) — canonical URL; defaults to window.location.href
 *  - priceText (string, optional) — pre-formatted price to include in message
 */
export const ShareButton = ({ listingId, title, url, priceText = '', className = '', size = 'sm', variant = 'outline' }) => {
  const [open, setOpen] = useState(false);
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const cleanTitle = (title || 'this listing').replace(/\s+/g, ' ').trim();
  const bodyText = priceText
    ? `${cleanTitle} — ${priceText} on IndusFact`
    : `${cleanTitle} on IndusFact`;
  const fullMessage = `${bodyText}: ${shareUrl}`;

  const track = (network) => {
    // Fire-and-forget — never block the share flow on this
    try {
      api.post(`/share/listing/${listingId}?network=${encodeURIComponent(network)}`).catch(() => {});
    } catch { /* ignore */ }
  };

  const openWindow = (href) => {
    try {
      window.open(href, '_blank', 'noopener,noreferrer,width=650,height=560');
    } catch { /* ignore */ }
  };

  const doNative = async () => {
    if (typeof navigator === 'undefined' || !navigator.share) return false;
    try {
      await navigator.share({ title: cleanTitle, text: bodyText, url: shareUrl });
      track('native');
      return true;
    } catch { /* user cancelled */ return true; }
  };

  const shareTo = async (network) => {
    setOpen(false);
    if (network === 'copy') {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied');
      } catch { toast.error('Copy failed'); }
      track('copy');
      return;
    }
    if (network === 'native') {
      const ok = await doNative();
      if (!ok) toast.info('Sharing not supported on this device');
      return;
    }
    const enc = encodeURIComponent;
    let href = '';
    switch (network) {
      case 'whatsapp':
        href = `https://api.whatsapp.com/send?text=${enc(fullMessage)}`;
        break;
      case 'linkedin':
        href = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`;
        break;
      case 'facebook':
        href = `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}&quote=${enc(bodyText)}`;
        break;
      case 'twitter':
        href = `https://twitter.com/intent/tweet?url=${enc(shareUrl)}&text=${enc(bodyText)}`;
        break;
      case 'telegram':
        href = `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(bodyText)}`;
        break;
      case 'email':
        href = `mailto:?subject=${enc(cleanTitle)}&body=${enc(fullMessage)}`;
        // For mailto we use location.href so it triggers the mail client
        window.location.href = href;
        track('email');
        return;
      default:
        return;
    }
    openWindow(href);
    track(network);
  };

  const hasNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const items = [
    { key: 'whatsapp', label: 'WhatsApp', Icon: MessageCircle, color: 'text-[#25D366]' },
    { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin, color: 'text-[#0A66C2]' },
    { key: 'facebook', label: 'Facebook', Icon: Facebook, color: 'text-[#1877F2]' },
    { key: 'twitter', label: 'X (Twitter)', Icon: Twitter, color: 'text-foreground' },
    { key: 'telegram', label: 'Telegram', Icon: Send, color: 'text-[#26A5E4]' },
    { key: 'email', label: 'Email', Icon: Mail, color: 'text-muted-foreground' },
    { key: 'copy', label: 'Copy link', Icon: Copy, color: 'text-muted-foreground' },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size={size}
          variant={variant}
          className={className}
          data-testid='share-button-trigger'
        >
          <Share2 className='h-4 w-4 mr-1.5' /> Share
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-64 p-2' align='end' data-testid='share-popover'>
        <div className='px-2 py-1.5 text-[11px] uppercase tracking-wider text-muted-foreground font-medium'>
          Share this listing
        </div>
        {hasNativeShare && (
          <button
            type='button'
            onClick={() => shareTo('native')}
            className='w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-secondary focus:bg-secondary focus:outline-none text-sm'
            data-testid='share-native-btn'
          >
            <Share2 className='h-4 w-4 text-primary' />
            <span>Device share sheet</span>
          </button>
        )}
        {items.map(({ key, label, Icon, color }) => (
          <button
            key={key}
            type='button'
            onClick={() => shareTo(key)}
            className='w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-secondary focus:bg-secondary focus:outline-none text-sm'
            data-testid={`share-${key}-btn`}
          >
            <Icon className={`h-4 w-4 ${color}`} />
            <span>{label}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

export default ShareButton;
